import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { searchIngredients, deleteIngredient, Ingredient } from '../adminApi';

const CATEGORIES = [
  'Produce',
  'Meat & Seafood',
  'Dairy & Eggs',
  'Bakery',
  'Pantry Staples',
  'Frozen',
  'Condiments & Sauces',
  'Beverages',
  'Other',
];

const IngredientList: React.FC = () => {
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchIngredients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await searchIngredients({
        query: searchQuery || undefined,
        page: page + 1,
        limit: rowsPerPage,
        category: category || undefined,
      });
      setIngredients(result.ingredients || []);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load ingredients');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page, rowsPerPage, category]);

  useEffect(() => {
    fetchIngredients();
  }, [fetchIngredients]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleDelete = async (ingredient: Ingredient) => {
    if (!window.confirm(`Are you sure you want to delete "${ingredient.name}"?`)) return;

    try {
      await deleteIngredient(ingredient.ingredient_id);
      fetchIngredients();
    } catch (err: any) {
      alert(err.message || 'Failed to delete ingredient');
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Ingredients
      </Typography>

      {/* Search and filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search ingredients..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="center">Recipe Count</TableCell>
              <TableCell align="center">Calories</TableCell>
              <TableCell align="center">Tracked</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : ingredients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No ingredients found
                </TableCell>
              </TableRow>
            ) : (
              ingredients.map((ing) => (
                <TableRow key={ing.ingredient_id} hover>
                  <TableCell>{ing.ingredient_id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {ing.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={ing.category || 'Other'} size="small" />
                  </TableCell>
                  <TableCell align="center">{ing.recipe_count || 0}</TableCell>
                  <TableCell align="center">{ing.calories || '-'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={ing.is_tracked ? 'Yes' : 'No'}
                      size="small"
                      color={ing.is_tracked ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(ing)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>
    </Box>
  );
};

export default IngredientList;
